import { Suspense } from "react"
import { createNativeStackNavigator } from "@react-navigation/native-stack"
import { RootStackParamList } from "./types.ts"
import { InitialIntroductionScreen } from "../screens/InitialIntroductionScreen.tsx"
import { InitialScreenV2 } from "../screens/InitialScreenV2.tsx"
import {
  AccountsScreen,
  BottomTabNavigator,
  EnableNotificationsScreen,
  InsuranceConnectionRouter,
  LoggedOutScreen,
  MissingBankConnection,
  NotificationsSettingsItemScreen,
  NotificationsSettingsScreen,
  PersonalDetailsScreen,
  PlanConsentRequestScreenWithGuard,
  PlanConsentSuccessScreen,
  RecipientDetailsScreen,
  ReimbursementsModalStackView,
  CardPaymentScreen,
  DownloadTheAppScreen,
} from "./loggedInScreens"
import {
  BankConnectionErrorScreenV2,
  CharityFlowRouter,
  DebitAuthPaymentScreen,
  GeneralErrorScreen,
  LogInRouter,
  OnboardingV2Router,
  PlaidAuthLinkFlowRouter,
} from "./flowScreens.web"
import {
  BusinessProfile,
  NotSupportedOnWebScreen,
  PaymentMethodScreen,
  PaymentRequestScreenWithGuard,
  PaymentSentSuccessScreen,
  PlaidOAuthReturnMissingScreen,
} from "./paymentScreens.web"
import { ScreenLoadingFallback } from "./ScreenLoadingFallback"
import { NavigationRoot } from "./NavigationRoot"
import { webLinking } from "./linkingConfig"

const Stack = createNativeStackNavigator<RootStackParamList>()

type StackNavigatorProps = {
  isLoggedIn: boolean
}

function WebStackNavigator({ isLoggedIn }: StackNavigatorProps) {
  function renderLoggedOutScreens() {
    return (
      <>
        <Stack.Screen name="Initial" component={InitialIntroductionScreen} />
        <Stack.Screen name="InitialV2" component={InitialScreenV2} />
        <Stack.Screen name="LogIn" component={LogInRouter} />
        <Stack.Screen name="SignUpFlow" component={OnboardingV2Router} />
        <Stack.Screen name="PlaidAuthLink" component={PlaidAuthLinkFlowRouter} />
        <Stack.Screen name="PaymentMethod" component={PaymentMethodScreen} />
        <Stack.Screen name="PaymentRequest" component={PaymentRequestScreenWithGuard} />
        <Stack.Screen name="PaymentSentSuccessScreen" component={PaymentSentSuccessScreen} />
        <Stack.Screen name="DownloadTheAppScreen" component={DownloadTheAppScreen} />
        <Stack.Screen name="BusinessProfile" component={BusinessProfile} />
        <Stack.Screen name="NotSupportedOnWeb" component={NotSupportedOnWebScreen} />
        <Stack.Screen name="PlaidOAuthReturnMissing" component={PlaidOAuthReturnMissingScreen} />
      </>
    )
  }

  function renderLoggedInScreens() {
    return (
      <>
        <Stack.Screen name="BottomTabScreens" component={BottomTabNavigator} />
        <Stack.Screen name="PaymentRequest" component={PaymentRequestScreenWithGuard} />
        <Stack.Screen name="PaymentMethod" component={PaymentMethodScreen} />
        <Stack.Screen name="PlanConsentRequest" component={PlanConsentRequestScreenWithGuard} />
        <Stack.Screen name="PlanConsentSuccess" component={PlanConsentSuccessScreen} />
        <Stack.Screen name="DownloadTheAppScreen" component={DownloadTheAppScreen} />
        <Stack.Screen name="MissingBankConnection" component={MissingBankConnection} />
        <Stack.Screen name="PaymentSentSuccessScreen" component={PaymentSentSuccessScreen} />
        <Stack.Screen name="RecipientDetails" component={RecipientDetailsScreen} />
        <Stack.Screen name="AccountSettings" component={AccountsScreen} />
        <Stack.Screen name="PersonalDetails" component={PersonalDetailsScreen} />
        <Stack.Screen name="NotificationsSettings" component={NotificationsSettingsScreen} />
        <Stack.Screen
          name="NotificationsSettingsItem"
          component={NotificationsSettingsItemScreen}
        />
        <Stack.Screen
          name="ReimbursementModalScreen"
          component={ReimbursementsModalStackView}
          options={{
            presentation: "modal",
            headerShown: false,
          }}
        />
        <Stack.Screen name="InsuranceConnection" component={InsuranceConnectionRouter} />
        <Stack.Screen name="BusinessProfile" component={BusinessProfile} />
        <Stack.Screen name="EnableNotifications" component={EnableNotificationsScreen} />
        <Stack.Screen name="LoggedOut" component={LoggedOutScreen} />
      </>
    )
  }

  return (
    <Stack.Navigator
      initialRouteName="Initial"
      screenOptions={{ headerShown: false, fullScreenGestureEnabled: false }}
    >
      {!isLoggedIn ? renderLoggedOutScreens() : renderLoggedInScreens()}
      <Stack.Screen name="CharityFlow" component={CharityFlowRouter} />
      <Stack.Screen name="GeneralError" component={GeneralErrorScreen} />
      <Stack.Screen name="BankConnectionError" component={BankConnectionErrorScreenV2} />
      <Stack.Screen name="DebitAuthPayment" component={DebitAuthPaymentScreen} />
      <Stack.Screen name="CardPayment" component={CardPaymentScreen} />
    </Stack.Navigator>
  )
}

export function Router() {
  return (
    <NavigationRoot linking={webLinking}>
      {(isLoggedIn) => (
        <Suspense fallback={<ScreenLoadingFallback />}>
          <WebStackNavigator isLoggedIn={isLoggedIn} />
        </Suspense>
      )}
    </NavigationRoot>
  )
}
